import React, { useEffect, useMemo, useState } from 'react';
import HBModal from '@honeybook/hbui/dist/components/HBModal/HBModal';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import HBTabs from '@honeybook/hbui/dist/components/HBTabs/HBTabs';
import Users16 from '@honeybook/icons/dist/icons/Users16';
import Web16 from '@honeybook/icons/dist/icons/Web16';
import { IFlowPublicConfiguration } from '~/types/model.types';
import PublishedTemplateModalContent from '~/components/Templates/components/FlowTemplates/PublishedTemplateModalContent';
import PrePublishTemplateModalContent from '~/components/Templates/components/FlowTemplates/PrePublishTemplateModalContent';
import { useRootStore } from '~/stores/useRootStore';
import styled from 'styled-components';
import MemberSharingModalContent from '~/components/Templates/components/FlowTemplates/MemberSharingModalContent';
import { observer } from 'mobx-react';
import useAnalytics from '~/common/useAnalytics';
import { useModal } from '~/providers/modal.provider';
import { getClickShareTemplateSource } from '~/components/Templates/templates.utils';
import GalleryEligibilityModalContent from '~/components/Templates/components/FlowTemplates/GalleryEligibilityModalContent';
import PublishTemplateModalContent from '~/components/Templates/components/FlowTemplates/PublishTemplateModalContent';
import is from 'styled-is';
import {
    Body,
    Header
} from '@honeybook/hbui/dist/components/HBModal/HBModal.styles';
import { useIsGateEnabled } from '~/common/Statsig/useIsGateEnabled';
import { mediumUp } from '@honeybook/hbui/dist/styles/breakpoints';
import SellingTemplateModalContent from '~/components/Templates/components/FlowTemplates/SellingTemplateModalContent';
import Dollar16 from '@honeybook/icons/dist/icons/Dollar16';

export interface PublicTemplateConfigurationProps {
    publicConfiguration: IFlowPublicConfiguration;
}

const PublicTemplateConfigurationModal = () => {
    const {
        templatesUiStore: { selectedTemplate, closeShareModal },
        builderUiStore: { selectedFlow }
    } = useRootStore();
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });
    const { closeModal } = useModal();
    const [tab, setTab] = useState('public_sharing_tab');
    const { track } = useAnalytics();
    const isGalleryPublishingAllowed = useIsGateEnabled({
        gateName: 'template_thumbnails_generation'
    });

    const isUsingMemberSharing = useIsGateEnabled({
        gateName: 'is_using_member_sharing'
    });

    const isPartner = useIsGateEnabled({
        gateName: 'template_selling'
    });

    const isPublished =
        selectedTemplate?.public_configuration?.published_template ||
        selectedFlow?.public_configuration?.published_template;

    useEffect(() => {
        let trackTab =
            tab === 'public_sharing_tab' ? 'public sharing' : 'member sharing';
        if (tab === 'public_sharing_tab') {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            trackTab = isPublished
                ? trackTab.concat(' - live link')
                : trackTab.concat(' - no link');
        }

        track('share modal tab', {
            action: 'load',
            source: getClickShareTemplateSource(),
            tab: trackTab,
            template_id: selectedTemplate?._id
        });
    }, [isPublished, selectedTemplate?._id, tab, track]);

    const shouldShowGalleryTab = isGalleryPublishingAllowed && isPublished;

    const tabs = useMemo(() => {
        if (!isUsingMemberSharing && !shouldShowGalleryTab) {
            return [];
        }

        const _tabs = [
            {
                title: t('public_sharing_tab'),
                icon: <Web16 />,
                id: 'public_sharing_tab'
            }
        ];

        if (isUsingMemberSharing) {
            _tabs.push({
                title: t('member_sharing_tab'),
                icon: <Users16 />,
                id: 'member_sharing_tab'
            });
        }

        if (shouldShowGalleryTab) {
            _tabs.push({
                title: t('gallery_tab_title'),
                icon: <Users16 />,
                id: 'gallery_tab'
            });
        }
        if (isPartner) {
            _tabs.push({
                title: t('selling_tab_title'),
                icon: <Dollar16 />,
                id: 'selling_template_tab'
            });
        }
        return _tabs;
    }, [isPartner, isUsingMemberSharing, shouldShowGalleryTab, t]);

    const renderModalByTab = useMemo(() => {
        const public_configuration =
            selectedTemplate?.public_configuration ||
            selectedFlow?.public_configuration;
        switch (tab) {
            case 'public_sharing_tab':
                return isPublished ? (
                    <PublishedTemplateModalContent
                        noTabs={tabs.length === 0}
                        publicConfiguration={public_configuration}
                    />
                ) : (
                    <PrePublishTemplateModalContent />
                );
            case 'member_sharing_tab':
                return <MemberSharingModalContent />;
            case 'gallery_tab':
                return (
                    <GalleryEligibilityModalContent
                        publicConfiguration={public_configuration}
                    />
                );
            case 'selling_template_tab':
                return (
                    <SellingTemplateModalContent
                        publicConfiguration={public_configuration}
                        isPadded
                    />
                );
        }
    }, [
        isPublished,
        selectedFlow?.public_configuration,
        selectedTemplate?.public_configuration,
        tab,
        tabs.length
    ]);

    if (!selectedTemplate && !selectedFlow) {
        return null;
    }

    const shouldShowOldModalContent =
        shouldShowGalleryTab || isUsingMemberSharing;

    const oldModalContent = (
        <>
            {!!tabs.length && (
                <HBTabs
                    testId="publish_template_tabs"
                    tabs={tabs}
                    onClick={setTab}
                    selectedTab={tab}
                    stretchToFill
                />
            )}

            {renderModalByTab}
        </>
    );

    return (
        <SharingModal
            shouldShowOldModalContent={shouldShowOldModalContent}
            isOpen
            title={isPartner ? t('selling_title') : t('title')}
            onClose={() => {
                closeModal('PublicTemplateConfiguration');
                closeShareModal();
            }}
            maxHeight="100%"
            isFullScreenOnMobile
            testId="share-template-modal"
        >
            {shouldShowOldModalContent ? (
                oldModalContent
            ) : (
                <PublishTemplateModalContent
                    publicConfiguration={
                        selectedTemplate?.public_configuration ||
                        selectedFlow?.public_configuration
                    }
                />
            )}
        </SharingModal>
    );
};
export default observer(PublicTemplateConfigurationModal);

const SharingModal = styled(HBModal)<{ shouldShowOldModalContent: boolean }>`
    ${mediumUp} {
        max-width: 464px;
    }
    ${Body} {
        overflow-y: unset;
    }
    ${is('shouldShowOldModalContent')`
      max-width: 483px;
        ${Header} {
            border-bottom: unset;
        }
        ${Body} {
            padding: 0;
            overflow-y: unset;
        }
    `}
`;
